import React, {useCallback, useEffect, useState} from 'react';
import MapBase from "./MapBase";
import useMapProps from "./useMapProps";
import {MapPropsProvider} from "./MapPropsContext";
import WidgetTableClicked from "./widgets/WidgetTableClicked";
import {arrayToRgb, formatUid, getApiUrl, makeTableSorterFn, numberWithCommas, positionToDegress} from "../../utils";
import WidgetContainer from "./widgets/WidgetContainer";
import WidgetBand from "./widgets/WidgetBand";
import RMateChartTableImpCheck from "./r_mate_chart/RMateChartTableImpCheck";
import {PALETTE_YlOrRd_9, TRANSPARENT_COLOR} from "../../utils/Palette";
import {WELL_KNOWN_POSITION} from "../../Constants";
import WidgetDistrictSelect from "./widgets/WidgetDistrictSelect";
import WidgetToolbar from "./widgets/WidgetToolbar";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetTable from "./widgets/WidgetTable";
import AlignRightText from "./widgets/AlignRightText";
import WidgetUidWithShortcut from "./widgets/WidgetUidWithShortcut";


const ICON_MAPPING = {
 '11': ['5g-sk-in', 'SKT 3.5G'],
 '21': ['5g-kt-in', 'KT 3.5G'],
 '31': ['5g-lg-in', 'LGU+ 3.5G'],
 '12': ['5g-sk-out', 'SKT 28G'],
 '22': ['5g-kt-out', 'KT 28G'],
 '32': ['5g-lg-out', 'LGU+ 28G'],
};


const ICON_CODE_BY_FRQ_CODE = Object.entries(ICON_MAPPING).reduce((acc, [frqCode, [iconCode]]) => {
  acc[frqCode] = iconCode;
  return acc;
}, {});

const FRQ_CODE_OPTIONS = Object.entries(ICON_MAPPING).map(([frqCode, [iconCode, iconLabel]]) => {
  return { frq_code: frqCode, label: iconLabel };
});

const ImpCheckMap= () => {
  const [fillColorLegend, setFillColorLegend] = useState();

  const mapProps = useMapProps({
    INITIAL_GEOJSON_EXTRA_PARAMS: {
      currentDistrictCode: '',
    },
    INITIAL_VIEW_STATE: {
      ...WELL_KNOWN_POSITION.세종특별자치시,
      zoom: 8,
    },
    INITIAL_GEOJSON_POLYGON_LAYER_PROPS: {
      getLevelFillColor: ({properties: {pk}}, mapDistrictStats) => {
        const [metricObject, metricMax] = mapDistrictStats;
        const code = pk;
        const obj = metricObject[code];
        if (!obj) return TRANSPARENT_COLOR;
        const {total} = (metricObject[code] || {total: 1});
        if (total === 0) return TRANSPARENT_COLOR;
        const index = Math.floor(PALETTE_YlOrRd_9.length * (total - 1) / metricMax);
        return PALETTE_YlOrRd_9[index];
      },
      getHoverHtml: ({properties: {pk, name}}, mapDistrictStats) => {
        const [metricObject] = mapDistrictStats;
        const code = pk;
        const obj = metricObject[code];
        if (!obj) return `${name} : 0 개`;
        const {total} = (metricObject[code] || {total: 1});
        return `${name} : ${numberWithCommas(total)} 개`;
      },
    },
    INITIAL_POINT_LAYER_PROPS: {
      getIconCode: ({frq, frq_hz}) => {
        try {
          return ICON_MAPPING[frq || frq_hz][0];
        }catch (e) {
          console.error("not found iconCode for", frq);
          return null;
        }
      },
      getIconWidth: (feature, zoom) => {
        let iconWidth = 30;

        if(zoom <=9) {
          return 12
        } else if (zoom <= 10) {
          return 15
        } else if (zoom <= 11) {
          return 18
        } else if (zoom <= 12) {
          return 21
        } else if (zoom <= 13) {
          return 24;
        } else if (zoom <= 16) {
          return 30;
        }else if (zoom >= 17) {
          return 34;
        } else {
          iconWidth -= iconWidth * (15 - Math.max(7, zoom)) / 14;
          return Math.min(30, iconWidth);
        }
      }
    },
    INITIAL_WIDGET_STATE: {

      frq_list: [
        '11',
        '21',
        '31',
        '12',
        '22',
        '32',
       ],
    },
  });

  const {
    clickedPointIconObjects,
    setClickedPointIconObjects,
    widgetState,
    currentDistrictCode,
    setCurrentDistrictCode,
    setPointsLayerFilterFunc,
    mapDistrictStats, setMapDistrictStats, isChangedMapDistrictStats,
    clickedPolygonGeojsonObjects,
    setGeojsonExtraParams,
    dataResponseList,
    pointsResponseList,
  } = mapProps;

  const {frq_list} = widgetState;
  const [sidoCode, setSidoCode] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  const [isNationwide, setIsNationwide] = useState(false);

  // 광역 단위인 경우 클러스터링 보기  / 시군구 단위인 경우 아이콘 보기
  // useEffect(() => {
  //   if(currentDistrictCode && currentDistrictCode.length === 2){
  //     setIsClusteringPoint(true);
  //   } else {
  //     setIsClusteringPoint(false);
  //   }
  // }, [currentDistrictCode]);

  // 전국 단위일 때, 목록보기 비활성화 처리합니다.
  useEffect(() => {
    if(!currentDistrictCode || currentDistrictCode === ""){
      setIsNationwide(true);
    } else {
      setIsNationwide(false);
    }
  }, [currentDistrictCode]);

  useEffect(() => {
    const {frq_list} = widgetState;
    const firstDataLayer = dataResponseList.length > 0 ? dataResponseList[0] : null;

    if (!firstDataLayer)
      return;

    const metricObject = {};
    if (firstDataLayer) {
      //26: {421.996875: 2, 422.000000: 4, 422.003125: 4, 422.009375: 1, 422.012500: 8, …}
      for (let [code, stats] of Object.entries(firstDataLayer)) {
        let total = 0;
        for (let [frqCode, cnt] of Object.entries(stats)) {
          if (frq_list.includes(frqCode)) {
            total += cnt;
          }
        }
        metricObject[code] = {total};
      }
    }

    setSidoCode((currentDistrictCode || '').length < 2
      ? ''
      : currentDistrictCode.substr(0, 2));

    const metricMax = Math.max(...Object.values(metricObject).map(obj => obj.total));

    const newMapDistrictStats = [metricObject, metricMax];
    if (isChangedMapDistrictStats(newMapDistrictStats)) {
      setMapDistrictStats(newMapDistrictStats);
    }
  }, [widgetState, currentDistrictCode, dataResponseList]);


  useEffect(() => {

    if (!currentDistrictCode) {
      if ( pointsResponseList.length >0){
        window.pointsResponseList = pointsResponseList;
        // eslint-disable-next-line array-callback-return
        const hiddenUid = pointsResponseList[0].map((value) => {
          const {frq, uid} = value;

          if (frq_list.includes(frq)){
            return uid;
          }
        });
      }
      setPointsLayerFilterFunc(() => ({frq}) => {
        return false
      });
    } else {
      setPointsLayerFilterFunc(() => ({frq, district_code}) => {
        if (!district_code) return false;
        return (
          frq_list.includes(frq) &&
          district_code.startsWith(currentDistrictCode)
        );
      });
    }
  }, [frq_list, currentDistrictCode, pointsResponseList]);

  useEffect(() => {
    const [metricObject, metricMax] = mapDistrictStats;
    if (window.isFinite(metricMax) && metricMax >= 9) {
      setFillColorLegend(
        [...Array(9).keys()].map(colorIndex => {
          const boundary = (metricMax / 9) * (colorIndex + 1);
          const index = Math.floor(PALETTE_YlOrRd_9.length * (boundary - 1) / metricMax);
          const colorRgb = arrayToRgb(PALETTE_YlOrRd_9[index]);
          return [colorRgb, boundary];
        }).reverse()
      );
    }
  }, [mapDistrictStats]);

  useEffect(() => {
    setGeojsonExtraParams(prev => ({
      ...prev,
      currentDistrictCode,
    }));
    if ((currentDistrictCode||'').length <= 2) {
      setIsVisible(false);
    }
  }, [currentDistrictCode]);



  useEffect(() => {
    if (clickedPolygonGeojsonObjects.length > 0) {
      const firstLayer = clickedPolygonGeojsonObjects[0];
      const {properties: {name, pk}} = firstLayer;
      setCurrentDistrictCode(pk);
      setSidoCode(pk.substr(0, 2));
      if ((pk||'').length <= 2){
        setIsVisible(false);
      }
    }
  }, [clickedPolygonGeojsonObjects]);

  const onClickedPointInWidgetTable = useCallback((feature) => {
    const {latitude, longitude, lat, lon} = feature;
    const newLatitude = positionToDegress(latitude || lat);
    const newLongitude = positionToDegress(longitude || lon);
    const newFeature = {...feature, position: [newLongitude, newLatitude]};
    setClickedPointIconObjects([newFeature]);
  }, [setClickedPointIconObjects]);

  return (
    <MapPropsProvider value={mapProps}>
      <MapBase isHideGeolocated={true}>
        <WidgetContainer>
          <div>
            <WidgetDistrictSelect isShowDistrictLayerFromGeoserver={true} />
            <WidgetBand options={FRQ_CODE_OPTIONS} iconCodeByFrq={ICON_CODE_BY_FRQ_CODE}/>
          </div>

        </WidgetContainer>
        <RMateChartTableImpCheck
          frqList={frq_list}
          sidoCode={sidoCode}
          iconMapping={ICON_MAPPING}
          disable={widgetState.frq_list.length === 0}
          isVisible={isVisible}
        />
        <WidgetTableClicked
          fetchUrl={getApiUrl({type: "type2"})}
          getTitle={objects => `${objects.length}개의 이행점검 무선국`}
          objects={clickedPointIconObjects}
          getRow={({uid, frq_hz, cus_fln, rds_new_adr,pci,rsrp}) =>
            <table>
              <tbody>
              <tr>
                <th>허가번호</th>
                <td>{formatUid(uid)}</td>
              </tr>
              <tr>
                <th>주파수(MHz)</th>
                <td>{frq_hz}</td>
              </tr>
              <tr>
                <th>시설자 명</th>
                <td>{cus_fln}</td>
              </tr>
              <tr>
                <th>설(상)치 장소</th>
                <td>{rds_new_adr}</td>
              </tr>
               <tr>
                <th>기지국ID</th>
                <td>{pci}</td>
              </tr>
               <tr>
                <th>전파수신레벨</th>
                <td>{rsrp}</td>
              </tr>
              </tbody>
            </table>
          }
        />

        <WidgetTable
          disabled={widgetState.frq_list.length === 0 || isNationwide}
          fetchUrl={getApiUrl({type: "type1"})}
          getTitle={(objects) => `${objects.length}개의 이행점검 무선국`}
          columns={[
            {title: '순번', dataIndex: 'rnum', key: 'rnum', width: 75, fixed: 'left', align: 'center', sorter: makeTableSorterFn('rnum')},
            {
              title: '허가번호', dataIndex: 'uid', key: 'uid', width: 180, fixed: 'left',
              render: (uid, feature) => WidgetUidWithShortcut({feature, onClick: onClickedPointInWidgetTable}),
              sorter: makeTableSorterFn('uid')
            },
            {title: '무선국 호출명', dataIndex: 'rds_cll_nm', key: 'rds_cll_nm', sorter: makeTableSorterFn('rds_cll_nm')},
            {title: '시설자 명', dataIndex: 'cus_fln', key: 'cus_fln', sorter: makeTableSorterFn('cus_fln')},
            {title: '전파형식코드', dataIndex: 'emdg_cd', key: 'emdg_cd', sorter: makeTableSorterFn('emdg_cd')},
            {title: '주파수(MHz)', dataIndex: 'frq_hz', key: 'frq_hz', sorter: makeTableSorterFn('frq_hz')
              , render: value => AlignRightText({text: value, numberComma: true})
            },
            {title: '신주소 설치장소', dataIndex: 'rds_new_adr', key: 'rds_new_adr', sorter: makeTableSorterFn('rds_new_adr')},
            {title: '위도', dataIndex: 'lat', key: 'lat'},
            {title: '경도', dataIndex: 'lon', key: 'lon'},
            {title: '기지국ID', dataIndex: 'pci', key: 'pci', sorter: makeTableSorterFn('pci')},
            {title: '전파수신레벨', dataIndex: 'rsrp', key: 'rsrp', sorter: makeTableSorterFn('rsrp')},

          ]}
        />
       <WidgetLegend fillColorLegend={fillColorLegend}  titleColorLegend={'무선국 수'}  unitColorLegend={'개'}/>

      </MapBase>
      <WidgetToolbar/>
    </MapPropsProvider>
  );
};


export default ImpCheckMap;